// Phase OWL-G1 — Continent flag rows (GAP 2).
//
// Renders Altitude + Rivalry flag rows below the coverage panel.
// Always renders both rows. Composes the I0 Pill primitive only.
// Data source: owl_hour_slips.continent_flags JSONB (never hardcoded).

import { Pill } from "../super-acca/Pill.tsx";
import {
  ALTITUDE_EMPTY_COPY,
  RIVALRY_EMPTY_COPY,
  type ParsedContinentFlags,
} from "../../owl/owlContinentFlags.ts";

export interface OwlContinentFlagRowsProps {
  flags: ParsedContinentFlags;
}

const ROW_STYLE = {
  display: "flex",
  flexWrap: "wrap" as const,
  alignItems: "center",
  gap: 8,
  padding: "10px 14px",
  borderBottom: "1px solid var(--owl-border)",
  fontSize: 12,
};

const LABEL_STYLE = {
  fontFamily: "var(--sa-font-stat)",
  fontSize: 11,
  letterSpacing: "0.14em",
  textTransform: "uppercase" as const,
  color: "var(--owl-text-3)",
  minWidth: 70,
};

export function OwlContinentFlagRows({
  flags,
}: OwlContinentFlagRowsProps): JSX.Element {
  const altActive = flags.altitude.active && flags.altitude.venues.length > 0;
  const rivActive = flags.rivalry.active && flags.rivalry.matches.length > 0;

  return (
    <div
      data-testid="owl-continent-flag-rows"
      style={{
        marginTop: 12,
        background: "var(--owl-elev)",
        border: "1px solid var(--owl-border)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      {/* ── Altitude row (AC-2.1) ─────────────── */}
      <div data-testid="owl-altitude-row" style={ROW_STYLE}>
        <span style={LABEL_STYLE}>⛰ Altitude</span>
        {altActive ? (
          flags.altitude.venues.map((v, i) => (
            <Pill
              key={`alt-${i}`}
              color="#f5b74a"
              borderColor="#8a5a14"
              background="rgba(245,183,74,0.08)"
            >
              {v.city} · {v.elevation_m.toLocaleString()}m
            </Pill>
          ))
        ) : (
          <Pill>{ALTITUDE_EMPTY_COPY}</Pill>
        )}
      </div>

      {/* ── Rivalry row (AC-2.2) ──────────────── */}
      <div data-testid="owl-rivalry-row" style={{ ...ROW_STYLE, borderBottom: "none" }}>
        <span style={LABEL_STYLE}>⚔ Rivalry</span>
        {rivActive ? (
          flags.rivalry.matches.map((m, i) => (
            <Pill
              key={`riv-${i}`}
              color="#f57a7a"
              borderColor="#8a2828"
              background="rgba(245,122,122,0.08)"
            >
              {m.match_name !== "" ? m.match_name : `${m.home} vs ${m.away}`}
            </Pill>
          ))
        ) : (
          <Pill>{RIVALRY_EMPTY_COPY}</Pill>
        )}
      </div>
    </div>
  );
}
